import React, { useState, useMemo, useEffect } from 'react';
import { KatButton } from '@amzn/katal-react';
import { BatcherService, FunctionMetadata, TERMINAL_JOB_STATUSES } from '../../services/Batcher/BatcherService';
import FunctionPickerModal from './FunctionPickerModal';
import SelectFunctionsTab from './tabs/SelectFunctionsTab';
import InputTab from './tabs/InputTab';
import SubmitTab from './tabs/SubmitTab';
import './BatcherPage.scss';

// How often to poll the job status while a submitted job is still running.
const JOB_POLL_INTERVAL_MS = 3000;

interface BatcherPageProps {
    stage: string;
    realm: string;
}

type StepId = 'functions' | 'input' | 'submit';

const STEPS: { id: StepId; label: string }[] = [
    { id: 'functions', label: 'Select Functions' },
    { id: 'input', label: 'Input' },
    { id: 'submit', label: 'Submit' },
];

const BatcherPage: React.FC<BatcherPageProps> = ({ stage, realm }) => {
    // Functions the user has added to the chain
    const [chain, setChain] = useState<FunctionMetadata[]>([]);
    // additionalArguments values per chain item, aligned by index with `chain`.
    const [chainArgs, setChainArgs] = useState<Record<string, string>[]>([]);
    // Which chain item's Additional Parameters box is open (null = none).
    const [selectedChainIndex, setSelectedChainIndex] = useState<number | null>(null);

    // Which step/tab is active. Navigation only happens via the Back/Next buttons.
    const [activeStep, setActiveStep] = useState<StepId>('functions');

    // Modal + picker state
    const [modalOpen, setModalOpen] = useState(false);
    const [availableFunctions, setAvailableFunctions] = useState<FunctionMetadata[]>([]);
    // Functions are fetched on mount and cached; the cache is invalidated whenever the
    // environment (stage/realm) changes so the picker never shows stale functions.
    const [functionsLoaded, setFunctionsLoaded] = useState(false);
    // Bump to force a re-fetch (e.g. retrying after a failed load).
    const [reloadToken, setReloadToken] = useState(0);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // CSV row input. The header is derived from the selected functions (see csvHeader
    // below) and is not editable.
    const [csvRows, setCsvRows] = useState('');

    // Submit state
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [jobId, setJobId] = useState('');

    // Job status polling state (populated after a successful submit).
    const [jobStatus, setJobStatus] = useState('');
    const [downloadUrl, setDownloadUrl] = useState('');
    const [jobError, setJobError] = useState('');
    // Row count captured at submit time — the denominator for the progress bar.
    const [submittedRowCount, setSubmittedRowCount] = useState(0);

    const PAGE_SIZE = 10;

    const filteredFunctions = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return availableFunctions;
        return availableFunctions.filter(fn => fn.functionName.toLowerCase().includes(term));
    }, [availableFunctions, search]);

    const totalPages = Math.max(1, Math.ceil(filteredFunctions.length / PAGE_SIZE));
    // Clamp the page in case filtering shrank the list below the current page.
    const currentPage = Math.min(page, totalPages - 1);
    const pageFunctions = filteredFunctions.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

    // CSV header: the INPUT field names of the FIRST function only. The CSV feeds just the head
    // of the chain — each subsequent function receives the previous function's output as its input
    // (e.g. JsonPath's `allData` is populated from the prior step, not a CSV column). Unioning every
    // function's inputs would wrongly demand downstream fields (e.g. `value,allData`) in the CSV.
    const csvHeader = useMemo(() => {
        const names: string[] = [];
        (chain[0]?.inputSchema || []).forEach(field => {
            if (!names.includes(field.name)) names.push(field.name);
        });
        return names.join(',');
    }, [chain]);

    // Step navigation state.
    const stepIndex = STEPS.findIndex(s => s.id === activeStep);
    const rowCount = csvRows.split('\n').filter(line => line.trim() !== '').length;
    const hasInputData = rowCount > 0;
    // A step must be completed before the user can advance from it.
    const stepComplete: Record<StepId, boolean> = {
        functions: chain.length > 0,
        input: hasInputData,
        submit: jobId !== '',
    };
    const canGoBack = stepIndex > 0;
    const canGoNext = stepIndex < STEPS.length - 1 && stepComplete[activeStep];

    // The download URL comes from an upstream service response (untrusted per SDO-19).
    // Allowlist the scheme before using it as a navigation href — reject anything but
    // https (e.g. a javascript:/data: URL) so it never reaches the <a href> sink.
    const safeDownloadUrl = useMemo(() => {
        if (!downloadUrl) return '';
        try {
            return new URL(downloadUrl).protocol === 'https:' ? downloadUrl : '';
        } catch {
            return '';
        }
    }, [downloadUrl]);

    // Fetch the function list whenever the environment changes (and on mount). The page
    // renders without waiting on this — the list is ready by the time the picker opens.
    // `reloadToken` lets the modal retry a failed load. Responses are guarded by a
    // cancelled flag so a slow previous-environment response can't overwrite the current one.
    useEffect(() => {
        let cancelled = false;
        setError('');
        setLoading(true);
        // Invalidate the cache for the new environment so the picker shows "Loading…"
        // rather than the previous environment's functions.
        setFunctionsLoaded(false);
        setAvailableFunctions([]);
        BatcherService.listFunctions(stage, realm)
            .then(functions => {
                if (cancelled) return;
                setAvailableFunctions(Array.isArray(functions) ? functions : []);
                setFunctionsLoaded(true);
            })
            .catch((e: any) => {
                if (!cancelled) setError(e.message || 'Failed to load functions');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [stage, realm, reloadToken]);

    const handleOpenModal = () => {
        setSearch('');
        setPage(0);
        setModalOpen(true);
        // Retry if the previous load failed (and isn't already retrying).
        if (!functionsLoaded && !loading) setReloadToken(t => t + 1);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(0);
    };

    const handleAddFunction = (fn: FunctionMetadata) => {
        setChain(prev => [...prev, fn]);
        setChainArgs(prev => [...prev, {}]);
        setModalOpen(false);
    };

    const handleRemoveFunction = (index: number) => {
        setChain(prev => prev.filter((_, i) => i !== index));
        setChainArgs(prev => prev.filter((_, i) => i !== index));
        // Keep the open Additional Parameters box pointing at the right item.
        setSelectedChainIndex(prev => {
            if (prev === null) return null;
            if (prev === index) return null;
            return prev > index ? prev - 1 : prev;
        });
    };

    const handleSelectChainItem = (index: number) => {
        setSelectedChainIndex(prev => (prev === index ? null : index));
    };

    const handleAdditionalArgChange = (index: number, name: string, value: string) => {
        setChainArgs(prev => {
            const next = [...prev];
            next[index] = { ...next[index], [name]: value };
            return next;
        });
    };

    const handleBack = () => {
        if (canGoBack) setActiveStep(STEPS[stepIndex - 1].id);
    };

    const handleNext = () => {
        if (canGoNext) setActiveStep(STEPS[stepIndex + 1].id);
    };

    const handleSubmit = async () => {
        setSubmitError('');
        setSubmitting(true);
        // Reset any previous job result before submitting a new one.
        setJobId('');
        setJobStatus('');
        setDownloadUrl('');
        setJobError('');
        setSubmittedRowCount(rowCount);
        try {
            const csv = `${csvHeader}\n${csvRows}`;
            const functionChain = chain.map((fn, index) => {
                // Include only non-empty additional argument values.
                const additionalArguments: Record<string, string> = {};
                Object.entries(chainArgs[index] || {}).forEach(([name, value]) => {
                    if (value.trim() !== '') additionalArguments[name] = value;
                });
                return {
                    functionIdentifier: fn.functionIdentifier,
                    additionalArguments,
                };
            });
            const id = await BatcherService.uploadAndSubmit(stage, realm, csv, functionChain);
            setJobId(id);
        } catch (e: any) {
            setSubmitError(e.message || 'Failed to submit job');
        } finally {
            setSubmitting(false);
        }
    };

    // Poll the job status once a job has been submitted, until it reaches a terminal state.
    useEffect(() => {
        if (!jobId) return undefined;

        let cancelled = false;
        let intervalId: ReturnType<typeof setInterval>;

        const poll = async () => {
            try {
                const result = await BatcherService.getJobResult(stage, realm, jobId);
                if (cancelled) return;
                setJobStatus(result.status);
                if (result.downloadUrl) setDownloadUrl(result.downloadUrl);
                if (result.error) setJobError(result.error);
                if (TERMINAL_JOB_STATUSES.includes(result.status)) clearInterval(intervalId);
            } catch (e: any) {
                if (cancelled) return;
                setJobError(e.message || 'Failed to fetch job status');
                clearInterval(intervalId);
            }
        };

        poll();
        intervalId = setInterval(poll, JOB_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [jobId, stage, realm]);

    return (
        <div className="batcher-page">
            <div className="batcher-header">
                <h1 className="batcher-title">Batcher</h1>
                <p className="batcher-subtitle">Select functions and provide input rows to run them as a chain.</p>
            </div>

            {/* Display-only step header. Tabs are not clickable — use Back/Next to navigate. */}
            <div className="batcher-steps" role="tablist" aria-label="Batcher steps">
                {STEPS.map((step, index) => (
                    <div
                        key={step.id}
                        role="tab"
                        aria-selected={step.id === activeStep}
                        className={`batcher-step${step.id === activeStep ? ' batcher-step-active' : ''}`}
                    >
                        <span className="batcher-step-number">{index + 1}</span>
                        <span className="batcher-step-label">{step.label}</span>
                    </div>
                ))}
            </div>

            {activeStep === 'functions' && (
                <SelectFunctionsTab
                    chain={chain}
                    chainArgs={chainArgs}
                    selectedChainIndex={selectedChainIndex}
                    loading={loading}
                    error={error}
                    onOpenModal={handleOpenModal}
                    onRemoveFunction={handleRemoveFunction}
                    onSelectChainItem={handleSelectChainItem}
                    onAdditionalArgChange={handleAdditionalArgChange}
                />
            )}

            {activeStep === 'input' && (
                <InputTab csvHeader={csvHeader} csvRows={csvRows} onCsvRowsChange={setCsvRows} />
            )}

            {activeStep === 'submit' && (
                <SubmitTab
                    chain={chain}
                    rowCount={rowCount}
                    submitting={submitting}
                    submitError={submitError}
                    jobId={jobId}
                    jobStatus={jobStatus}
                    jobError={jobError}
                    safeDownloadUrl={safeDownloadUrl}
                    submittedRowCount={submittedRowCount}
                    onSubmit={handleSubmit}
                />
            )}

            {/* Hide navigation once a job has been submitted — no going back from a submitted job. */}
            {!jobId && (
                <div className="batcher-nav">
                    <KatButton className="batcher-nav-back" variant="secondary" onClick={handleBack} disabled={!canGoBack}>
                        Back
                    </KatButton>
                    <KatButton className="batcher-nav-next" variant="primary" onClick={handleNext} disabled={!canGoNext}>
                        Next
                    </KatButton>
                </div>
            )}

            <FunctionPickerModal
                open={modalOpen}
                loading={loading}
                error={error}
                search={search}
                availableFunctions={availableFunctions}
                filteredFunctions={filteredFunctions}
                pageFunctions={pageFunctions}
                currentPage={currentPage}
                totalPages={totalPages}
                onSearchChange={handleSearchChange}
                onPagePrev={() => setPage(p => Math.max(0, p - 1))}
                onPageNext={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                onAddFunction={handleAddFunction}
                onClose={() => setModalOpen(false)}
            />
        </div>
    );
};

export default BatcherPage;
